import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/*
 * Briefings, configurable client intake forms. The architect sends a link;
 * the client fills it out; the answers come back into Planno bound to a new
 * contact + lead, ready to convert into a proposal.
 *
 * Copy is locale-keyed — see the note in `crm-section.tsx`. The English
 * intake form asks for a phone number rather than a WhatsApp number, and
 * measures in square feet.
 */

type Field = { filled: boolean; label: string; placeholder: string }

type BriefingsContent = {
  eyebrow: string
  heading: string
  intro: string
  formEyebrow: string
  formTitle: string
  formSubtitle: string
  fields: readonly Field[]
  submitLabel: string
  templates: { title: string; body: string; chips: readonly string[] }
  lead: { title: string; body: string; name: string; meta: string; time: string }
  ai: { title: string; body: string; action: string }
}

const CONTENT: Record<MarketingLocale, BriefingsContent> = {
  en: {
    ai: {
      action: 'Suggest a scope from the brief',
      body: 'Take the answers and generate a considered proposal in seconds. The scope arrives already assembled.',
      title: 'Brief → proposal with AI',
    },
    eyebrow: 'BRIEFS',
    fields: [
      { filled: true, label: 'Full name', placeholder: 'Marion Vaughan' },
      { filled: true, label: 'Phone', placeholder: '(312) 555-0147' },
      { filled: true, label: 'Property address', placeholder: '800 N Michigan Ave · Chicago' },
      { filled: true, label: 'Project type', placeholder: 'Residential · Full renovation' },
      { filled: true, label: 'Approximate area', placeholder: '1,900 sq ft' },
      { filled: false, label: 'Expected budget', placeholder: '$80–120k' },
      { filled: false, label: 'Inspiration (link or photos)', placeholder: 'pinterest.com/…' },
    ],
    formEyebrow: 'WHITFIELD STUDIO · PROJECT BRIEF',
    formSubtitle: 'Takes about 4 minutes. Your answers go straight to the team.',
    formTitle: 'Let’s start with your project',
    heading: 'The project starts before the first meeting',
    intro:
      'Send a brief on a link and get structured answers back, ready to become a contact, a proposal, and a project.',
    lead: {
      body: 'The response automatically creates the contact and opens an opportunity in the CRM with its source, first message, and context.',
      meta: 'New lead · via brief',
      name: 'Marion Vaughan',
      time: 'Today 2:32 PM',
      title: 'Becomes a CRM lead, with its source',
    },
    submitLabel: 'Send to Whitfield Studio',
    templates: {
      body: 'Residential, commercial, renovation, consultation. Each category with questions calibrated for it. Edit whatever you like.',
      chips: ['Residential', 'Commercial', 'Renovation', 'Interiors', 'Consultation'],
      title: 'Templates by project type',
    },
  },
  'pt-BR': {
    ai: {
      action: 'Sugerir escopo a partir do briefing',
      body: 'Pegue as respostas e gere uma proposta inteligente em segundos. O escopo já vem montado.',
      title: 'Briefing → proposta com IA',
    },
    eyebrow: 'BRIEFINGS',
    fields: [
      { filled: true, label: 'Nome completo', placeholder: 'Mariana Vieira' },
      { filled: true, label: 'WhatsApp', placeholder: '(11) 98765-4321' },
      { filled: true, label: 'Endereço do imóvel', placeholder: 'Alameda Santos, 800 · SP' },
      { filled: true, label: 'Tipo de projeto', placeholder: 'Residencial · Reforma completa' },
      { filled: true, label: 'Metragem aproximada', placeholder: '180 m²' },
      { filled: false, label: 'Investimento previsto', placeholder: 'R$ 80–120 mil' },
      { filled: false, label: 'Inspirações (link ou fotos)', placeholder: 'pinterest.com/…' },
    ],
    formEyebrow: 'ESTÚDIO LOPES · BRIEFING',
    formSubtitle: 'Leva uns 4 minutos. Suas respostas vão direto para a equipe.',
    formTitle: 'Vamos começar pelo seu projeto',
    heading: 'O projeto começa antes da primeira reunião',
    intro:
      'Mande um link de briefing pelo WhatsApp e receba as respostas estruturadas, prontas para virar contato, proposta e projeto.',
    lead: {
      body: 'A resposta cria automaticamente o contato e abre uma oportunidade no CRM com origem, primeira mensagem e contexto.',
      meta: 'Novo lead · via briefing',
      name: 'Mariana Vieira',
      time: 'Hoje 14:32',
      title: 'Vira lead no CRM, com origem',
    },
    submitLabel: 'Enviar para o Estúdio Lopes',
    templates: {
      body: 'Briefing residencial, comercial, de reforma, de consultoria. Cada categoria com perguntas calibradas. Edite o que quiser.',
      chips: ['Residencial', 'Comercial', 'Reforma', 'Interiores', 'Consultoria'],
      title: 'Templates por tipo de projeto',
    },
  },
}

const BriefingMockup = ({ content }: { content: BriefingsContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_24px_60px_-26px_rgba(180,140,40,0.32)]">
    <div className="border-[#f1f1f1] border-b bg-gradient-to-br from-[#fef8e0] to-white px-5 py-4">
      <p className="text-[#a87a18] text-[10.5px] tracking-[0.18em]">{content.formEyebrow}</p>
      <p className="mt-2 font-bold text-[#1c1c20] text-[19px] leading-tight">{content.formTitle}</p>
      <p className="mt-1 text-[#6c6c72] text-[11.5px]">{content.formSubtitle}</p>
    </div>
    <div className="space-y-3 px-5 py-4">
      {content.fields.map((field) => (
        <div key={field.label}>
          <p className="text-[#6c6c72] text-[10.5px]">{field.label}</p>
          <div
            className="mt-1 rounded-lg border px-3 py-2 text-[11.5px]"
            style={{
              background: field.filled ? '#fff8e6' : '#fafafa',
              borderColor: field.filled ? '#e8c98a' : '#ececec',
              color: field.filled ? '#1c1c20' : '#b4b4b9',
            }}
          >
            {field.placeholder}
          </div>
        </div>
      ))}
      <div className="rounded-lg bg-[#f6cf5b] py-2 text-center font-semibold text-[#1c1c20] text-[12px]">
        {content.submitLabel}
      </div>
    </div>
  </div>
)

const Card = ({
  title,
  body,
  children,
}: {
  title: string
  body: string
  children: React.ReactNode
}) => (
  <div className="rounded-2xl border border-[#ece6d8] bg-white/80 p-6">
    <h3 className="font-bold text-[#1c1c20] text-[17px]">{title}</h3>
    <p className="mt-2 text-[#8a8a90] text-[14.5px] leading-relaxed">{body}</p>
    <div className="mt-5">{children}</div>
  </div>
)

export const BriefingsSection = () => {
  const content = useLocalized(CONTENT)

  return (
    <section className="relative overflow-hidden bg-[#fdf6e7] py-28">
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="briefings"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <p className="text-[#a87a18] text-[12px] tracking-[0.2em]">{content.eyebrow}</p>
          <h2 className="mx-auto mt-3 max-w-[820px] font-display text-[#0b0b0d] text-[clamp(40px,6vw,58px)] leading-[1.06] tracking-[-0.01em]">
            {content.heading}
          </h2>
          <p className="mx-auto mt-5 max-w-[640px] text-[#6c6c72] text-[clamp(16px,2.2vw,20px)]">
            {content.intro}
          </p>
        </ScrollReveal>

        <div className="mt-14 grid items-start gap-10 md:grid-cols-2">
          <ScrollReveal>
            <BriefingMockup content={content} />
          </ScrollReveal>
          <ScrollReveal className="space-y-6" delay={120}>
            <Card body={content.templates.body} title={content.templates.title}>
              <div className="flex flex-wrap gap-2">
                {content.templates.chips.map((t, i) => (
                  <span
                    className="rounded-md px-2.5 py-1 font-medium text-[11px]"
                    key={t}
                    style={{
                      background: i === 0 ? '#a87a18' : '#fff',
                      border: i === 0 ? 'none' : '1px solid #e8c98a',
                      color: i === 0 ? '#fff' : '#a87a18',
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </Card>
            <Card body={content.lead.body} title={content.lead.title}>
              <div className="rounded-lg border border-[#ece6d8] bg-white p-3">
                <div className="flex items-center gap-2">
                  <span className="size-7 rounded-full bg-[#fde9b8]" />
                  <div>
                    <p className="font-medium text-[#1c1c20] text-[11.5px]">{content.lead.name}</p>
                    <p className="text-[#9a9aa0] text-[10px]">{content.lead.meta}</p>
                  </div>
                  <span className="ml-auto rounded bg-[#fef8e0] px-1.5 py-0.5 text-[#a87a18] text-[10px]">
                    {content.lead.time}
                  </span>
                </div>
              </div>
            </Card>
            <Card body={content.ai.body} title={content.ai.title}>
              <div className="flex items-center gap-2 rounded-lg bg-[#fff8e6] px-3 py-2">
                <span className="flex size-6 items-center justify-center rounded-md bg-[#f6cf5b] text-[#1c1c20] text-[12px]">
                  ✦
                </span>
                <span className="text-[#43434a] text-[11.5px]">{content.ai.action}</span>
              </div>
            </Card>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
